// Single source of truth for the client-side phone number rule, applied
// everywhere a phone number is entered as country code + number (tradie and
// company sign-up, the settings phone-change flow) or as a single field
// (business admin profile edit). Mirrors the digits/length rule expected on
// tradiepass-api.

import PhoneNumber from 'awesome-phonenumber'

export const MIN_PHONE_DIGITS = 8
export const MAX_PHONE_DIGITS = 15

export const PHONE_LENGTH_ERROR = `Enter a valid phone number (${MIN_PHONE_DIGITS}-${MAX_PHONE_DIGITS} digits).`

// Every phone field in this app is hardcoded to default-country="AU" - used
// as a display-only fallback for a stored value that doesn't parse as a
// genuinely valid number (test/placeholder data, e.g. "61993939939" isn't a
// real AU prefix), so a redundant dial code still gets stripped instead of
// silently left in place just because the number itself is junk.
const DEFAULT_DIAL_CODE = '61'
const DEFAULT_ISO2 = 'AU'

/**
 * Strips everything but digits from a value.
 * @param {string|number|null|undefined} value
 * @returns {string}
 */
export function cleanPhoneDigits(value) {
  return String(value == null ? '' : value).replace(/\D/g, '')
}

/**
 * Turns a stored combined value like "61400151367" into a "+"-prefixed
 * E.164 string ("+61400151367") for prefilling a country-picker input that
 * parses by E.164 - the "+" makes the country unambiguous regardless of
 * which country the picker defaults to, unlike handing it the bare digits.
 * Returns '' for an empty value, not "+" alone.
 * @param {string|number|null|undefined} stored
 * @returns {string}
 */
export function e164FromStored(stored) {
  const digits = cleanPhoneDigits(stored)
  return digits ? `+${digits}` : ''
}

/**
 * Cleans a separate country-code field and number field down to their
 * digit-only parts: strips non-digits from both, drops a leading 0 from the
 * number (local Australian numbers are written without it once a country
 * code is present), and drops the country code again from the front of the
 * number if the user typed it twice (e.g. cc "61" + number "61433...").
 * @param {string|number} cc
 * @param {string|number} no
 * @returns {{cleanCc: string, cleanNo: string}}
 */
function splitCleanPhone(cc, no) {
  const cleanCc = cleanPhoneDigits(cc)
  let cleanNo = cleanPhoneDigits(no).replace(/^0+/, '')
  if (cleanCc && cleanNo.startsWith(cleanCc)) {
    cleanNo = cleanNo.slice(cleanCc.length)
  }
  return { cleanCc, cleanNo }
}

/**
 * Builds a clean phone number from a separate country-code field and number
 * field - see splitCleanPhone() for the cleanup rules.
 * @param {string|number} cc
 * @param {string|number} no
 * @returns {string}
 */
export function buildPhoneNumber(cc, no) {
  const { cleanCc, cleanNo } = splitCleanPhone(cc, no)
  return cleanCc + cleanNo
}

/**
 * Whether a phone number (already digits-only, as a single value - no
 * separate country-code field) is within the accepted length range.
 * Empty/undefined values are invalid, not "unset".
 * @param {string|number|null|undefined} digits
 * @returns {boolean}
 */
export function isValidPhoneLength(digits) {
  const clean = cleanPhoneDigits(digits)
  return clean.length >= MIN_PHONE_DIGITS && clean.length <= MAX_PHONE_DIGITS
}

/**
 * Whether a country-code + number pair is valid. The minimum digit count
 * applies to the number field alone, not the combined total - otherwise a
 * too-short number can "borrow" digits from the country code to scrape past
 * the minimum (e.g. cc "61" + no "423424" is 8 digits combined but is really
 * only a 6-digit number, too short to be real). The maximum still applies to
 * the combined total, matching E.164's overall length limit.
 * @param {string|number} cc
 * @param {string|number} no
 * @returns {boolean}
 */
export function isValidSplitPhone(cc, no) {
  const { cleanCc, cleanNo } = splitCleanPhone(cc, no)
  return (
    cleanNo.length >= MIN_PHONE_DIGITS &&
    (cleanCc + cleanNo).length <= MAX_PHONE_DIGITS
  )
}

/**
 * `@keydown` handler that stops a non-digit character from ever being typed
 * into a phone field, rather than letting it in and flagging it after the
 * fact. Deliberately doesn't touch the v-model value itself (see
 * updatePhoneValidity() in the components that use this) - filtering at the
 * key level, before the character reaches the input, avoids the desync bug
 * that came from rewriting a v-model'd input's own bound value while the
 * user is still typing into it.
 * @param {KeyboardEvent} event
 */
export function blockNonDigitKey(event) {
  // Multi-character key names (Backspace, Delete, ArrowLeft, Tab, Enter,
  // Home, End, ...) are navigation/editing keys, not characters - let them
  // through. Anything with a modifier (Ctrl/Cmd+A/C/V/X, etc.) too.
  if (event.ctrlKey || event.metaKey || event.altKey) return
  if (event.key.length !== 1) return
  if (!/[0-9]/.test(event.key)) event.preventDefault()
}

/**
 * `@paste` handler that strips non-digits out of pasted text instead of
 * letting it paste in raw. Replaces the current selection in the target
 * field with the cleaned digits.
 * @param {ClipboardEvent} event
 * @param {Vue} vm - the component instance (for `this[field]`)
 * @param {string} field - the data property name being pasted into
 */
export function sanitizePhonePaste(event, vm, field) {
  event.preventDefault()
  const clipboard = event.clipboardData || window.clipboardData
  const pasted = clipboard ? clipboard.getData('text') : ''
  const digits = cleanPhoneDigits(pasted)
  const current = vm[field] || ''
  const input = event.target
  const start =
    input && input.selectionStart != null
      ? input.selectionStart
      : current.length
  const end =
    input && input.selectionEnd != null ? input.selectionEnd : current.length
  vm[field] = current.slice(0, start) + digits + current.slice(end)
}

/**
 * Converts a 2-letter ISO region code ("AU") into its flag emoji ("🇦🇺") via
 * the regional indicator symbol trick (each letter A-Z maps to a Unicode
 * regional indicator codepoint, and a pair of them renders as a flag).
 * @param {string} iso2
 * @returns {string}
 */
export function flagEmojiForIso2(iso2) {
  if (!iso2 || iso2.length !== 2) return ''
  const codePoints = iso2
    .toUpperCase()
    .split('')
    .map((c) => 127397 + c.charCodeAt(0))
  return String.fromCodePoint(...codePoints)
}

/**
 * Groups local digits (no dial code, no leading trunk 0) the way that
 * country naturally groups them (AU mobile -> "400 151 366", AU landline ->
 * "2 1234 5678"), by round-tripping through awesome-phonenumber's
 * `AsYouType` formatter with the dial code re-attached, then slicing the
 * dial code back off. This reuses libphonenumber's own "international"
 * grouping rules instead of inventing (and getting wrong) our own
 * per-number-type grouping. Falls back to the raw digits if the country
 * isn't recognised or there's nothing to group.
 * @param {string} localDigits
 * @param {string|number} dialCode
 * @param {string} [iso2]
 * @returns {string}
 */
export function groupLocalPhoneDigits(localDigits, dialCode, iso2) {
  const digits = cleanPhoneDigits(localDigits)
  const dc = cleanPhoneDigits(dialCode)
  if (!digits || !dc) return digits
  try {
    const ayt = PhoneNumber.getAsYouType(iso2 || 'AU')
    const withCc = ayt.reset(`+${dc}${digits}`)
    const prefix = `+${dc}`
    const grouped = withCc.startsWith(prefix)
      ? withCc.slice(prefix.length).trim()
      : withCc
    // AsYouType only groups digits that match a real national number
    // pattern - a run that doesn't (test/placeholder data, e.g.
    // "993939939" isn't a real AU prefix) comes back with no spaces at
    // all, reading as a dense, differently-styled blob next to every
    // other field's properly-grouped number. Fall back to a plain 3-digit
    // chunking so it's still broken up, even though it isn't a real
    // number's actual grouping.
    return grouped === digits ? chunkDigits(digits) : grouped
  } catch (e) {
    return chunkDigits(digits)
  }
}

/**
 * Breaks a run of digits into 3-digit chunks ("993939939" -> "993 939
 * 939") as a generic fallback grouping - see groupLocalPhoneDigits().
 * @param {string} digits
 * @returns {string}
 */
function chunkDigits(digits) {
  return (digits.match(/.{1,3}/g) || []).join(' ')
}

/**
 * Strips a duplicated dial code and/or a leading trunk 0 off raw local
 * digits, e.g. (dialCode "61") "61400151366" -> "400151366", or
 * "0400151366" -> "400151366".
 * @param {string} rawDigits
 * @param {string|number} dialCode
 * @returns {string}
 */
export function stripPhoneNoise(rawDigits, dialCode) {
  let digits = cleanPhoneDigits(rawDigits)
  const dc = cleanPhoneDigits(dialCode)
  if (dc && digits.startsWith(dc) && digits.length > dc.length) {
    digits = digits.slice(dc.length)
  }
  return digits.replace(/^0+/, '')
}

/**
 * Shared resolution behind formatPhoneForDisplay()/formatPhoneWithDialCode():
 * best-effort dial code + region + grouped local number for a stored
 * combined value, falling back to the AU default for anything that doesn't
 * parse as a genuinely valid number (test/placeholder data) rather than
 * giving up and leaving noise in place.
 * @param {string|number|null|undefined} stored
 * @returns {{digits: string, dialCode: string, local: string}|null}
 */
function resolvePhoneDisplay(stored) {
  const digits = cleanPhoneDigits(stored)
  if (!digits) return null
  let pn
  try {
    pn = PhoneNumber(`+${digits}`)
  } catch (e) {
    pn = null
  }
  const valid = pn && pn.isValid()
  const dialCode = valid ? String(pn.getCountryCode()) : DEFAULT_DIAL_CODE
  const iso2 = valid ? pn.getRegionCode() : DEFAULT_ISO2
  const local = stripPhoneNoise(digits, dialCode)
  return {
    digits,
    dialCode,
    local: groupLocalPhoneDigits(local, dialCode, iso2) || local,
  }
}

/**
 * Display-only formatter: takes a stored combined value ("61400151367") and
 * returns the grouped, noise-free local number with no flag or dial code
 * ("400 151 367") - vue-tel-input's own flag/dial-code picker already shows
 * that part, so this is what should sit inside the field/label itself.
 * Falls back to the raw digits for a value that can't be parsed as valid,
 * so something still renders instead of blanking out.
 * @param {string|number|null|undefined} stored
 * @returns {string}
 */
export function formatPhoneForDisplay(stored) {
  const resolved = resolvePhoneDisplay(stored)
  return resolved ? resolved.local : ''
}

/**
 * Display-only formatter for places with no separate flag/dial-code picker
 * (plain read-only profile/detail views, not a vue-tel-input field) -
 * returns the dial code and grouped local number together
 * ("+61 400 151 367").
 * @param {string|number|null|undefined} stored
 * @returns {string}
 */
export function formatPhoneWithDialCode(stored) {
  const resolved = resolvePhoneDisplay(stored)
  return resolved ? `+${resolved.dialCode} ${resolved.local}` : ''
}

/**
 * Best-effort split of a stored combined value ("61400151367") into its
 * display parts: the flag, the "+"-prefixed dial code, and the grouped,
 * noise-free local number ("400 151 367"). Returns null when the value
 * can't be parsed as a valid number - callers should fall back to a raw
 * display or a placeholder, not treat that as a bug: numbers that predate
 * validation, or test data, can be unparsable.
 * @param {string|number|null|undefined} stored
 * @returns {{flag: string, dialCode: string, number: string}|null}
 */
export function phoneDisplayParts(stored) {
  const digits = cleanPhoneDigits(stored)
  if (!digits) return null
  let pn
  try {
    pn = PhoneNumber(`+${digits}`)
  } catch (e) {
    return null
  }
  if (!pn.isValid()) return null
  const dialCode = String(pn.getCountryCode())
  const iso2 = pn.getRegionCode()
  const local = stripPhoneNoise(digits, dialCode)
  return {
    flag: flagEmojiForIso2(iso2),
    dialCode: `+${dialCode}`,
    number: groupLocalPhoneDigits(local, dialCode, iso2),
  }
}

/**
 * Strips a redundant "+<dialCode> " prefix off a vue-tel-input field's
 * current text - used to drop the dial code from the box once the library
 * has auto-reformatted it to `mode="international"` (which bakes the dial
 * code into the displayed text), since the flag/code is already shown by
 * the picker itself right next to it.
 * @param {string} text
 * @param {string|number} dialCode
 * @returns {string}
 */
export function stripDialCodePrefix(text, dialCode) {
  const dc = cleanPhoneDigits(dialCode)
  if (!dc) return text
  return String(text || '').replace(new RegExp(`^\\+${dc}\\s*`), '')
}

/**
 * Keeps a vue-tel-input field's own internal `phone` text free of a
 * redundant dial-code prefix - reactively, for the lifetime of the
 * component, rather than trying to catch it from a single @input/@validate
 * event.
 *
 * Why: the library's own `phoneObject.valid` watcher rewrites its internal
 * `phone` (what the box actually renders) to mode="international" - dial
 * code baked in, e.g. "+61 400 151 367" - every time a number becomes
 * valid, including on mount for an already-valid prefilled number. That
 * rewrite never emits an `input` event, so a parent's v-model can't see or
 * correct it, and correcting from @input/@validate handlers races that
 * same internal watcher - whoever runs second wins, and depending on
 * exactly when validity flips during typing (vs. on mount), either side can
 * win. A `$watch` on the child's own `phone` property has no such race: it
 * always fires strictly after the value has actually changed, regardless
 * of what changed it or when, so the correction can never be a step behind.
 *
 * Call once from `mounted()`: installPhoneDisplayWatch(this, 'refName').
 * @param {Vue} vm - the parent component instance
 * @param {string} refName - the vue-tel-input's `ref` name
 */
export function installPhoneDisplayWatch(vm, refName) {
  vm.$watch(
    () => {
      const vti = vm.$refs[refName]
      return vti ? vti.phone : null
    },
    (phone) => {
      const vti = vm.$refs[refName]
      if (!vti || !phone) return
      const dialCode = vti.activeCountry && vti.activeCountry.dialCode
      const stripped = stripDialCodePrefix(phone, dialCode)
      if (stripped && stripped !== phone) {
        vti.phone = stripped
      }
    },
  )
}

/**
 * Lays out a phone number the API has already masked to its last three digits
 * ("XXXXXXXX366") the way an Australian number reads: "+61 XXX XXX 366". A
 * value that is not that shape (an email, another length) comes back as is.
 * @param {string|null|undefined} masked
 * @returns {string}
 */
export function formatMaskedPhone(masked) {
  const value = masked == null ? '' : String(masked)
  const match = /^X+(\d{3})$/.exec(value)
  if (!match) return value
  const last = match[1]
  if (value.length === 11) return `+61 XXX XXX ${last}`
  if (value.length === 10) return `XXXX XXX ${last}`
  return value
}
